/**
 * Backward-compatible hydration: turn a saved PolicyDetails back into the new
 * mode-based wizard state. Best-effort; falls back to defaults on shape drift.
 */

import type { PolicyDetails } from "@/types/api";
import {
  SU_KIND_PINNED,
  SU_KIND_SETTINGS,
  SoftwareUpdateWizardState,
  SuMode,
  SuPlatform,
  defaultBehavior,
  defaultPinned,
  defaultRolling,
  defaultWizardState,
} from "./types";

function parsePlatform(raw?: string | null): SuPlatform {
  const s = String(raw ?? "").toLowerCase();
  if (s.includes("macos")) return "macos";
  if (s.includes("ipados")) return "ipados";
  if (s.includes("vision")) return "visionos";
  if (s.includes("tvos")) return "tvos";
  if (s.includes("ios")) return "ios";
  return "macos";
}

export function fromPolicy(p: PolicyDetails): SoftwareUpdateWizardState {
  const state = defaultWizardState();
  state.name = p.name ?? "";
  state.description = (p.description as string) ?? "";

  const platforms = Array.isArray(p.platforms) ? p.platforms : [];
  if (platforms.length) state.platform = parsePlatform(platforms[0]);

  const payload = (p.payloads ?? [])[0];
  if (!payload) return state;

  const kind = String(payload.kind ?? "").toLowerCase();
  let mode: SuMode = "pinned";
  if (kind.includes("settings")) mode = "update_behavior";
  state.mode = mode;

  let cfg: Record<string, unknown> = {};
  if (payload.configJson) {
    try {
      cfg = JSON.parse(payload.configJson);
    } catch {
      cfg = {};
    }
  }

  if (mode === "pinned") {
    state.pinned = {
      ...defaultPinned(),
      targetVersion: String(cfg.TargetOSVersion ?? ""),
      targetBuild: String(cfg.TargetBuildVersion ?? ""),
      deadline: String(cfg.TargetLocalDateTime ?? ""),
      detailsUrl: String(cfg.DetailsURL ?? ""),
    };
  } else {
    const b = defaultBehavior();
    const def = (cfg.Deferrals ?? {}) as Record<string, unknown>;
    b.deferMajorDays = Number(def.MajorPeriodInDays ?? 0) || 0;
    b.deferMinorDays = Number(def.MinorPeriodInDays ?? 0) || 0;
    b.deferSystemDays = Number(def.SystemPeriodInDays ?? 0) || 0;
    b.deferCombinedDays = Number(def.CombinedPeriodInDays ?? 0) || 0;
    b.recommendedCadence = (cfg.RecommendedCadence as typeof b.recommendedCadence) || "";
    const auto = (cfg.AutomaticActions ?? {}) as Record<string, unknown>;
    if (auto.Download) b.autoDownload = auto.Download as typeof b.autoDownload;
    if (auto.InstallOSUpdates) b.autoInstallOs = auto.InstallOSUpdates as typeof b.autoInstallOs;
    if (auto.InstallSecurityUpdate)
      b.autoInstallSecurity = auto.InstallSecurityUpdate as typeof b.autoInstallSecurity;
    const rsr = (cfg.RapidSecurityResponse ?? {}) as Record<string, unknown>;
    if (typeof rsr.Enable === "boolean") b.rsrEnable = rsr.Enable;
    if (typeof rsr.EnableRollback === "boolean") b.rsrEnableRollback = rsr.EnableRollback;
    if (typeof cfg.AllowStandardUserOSUpdates === "boolean")
      b.allowStandardUserOsUpdates = cfg.AllowStandardUserOSUpdates as boolean;
    if (typeof cfg.Notifications === "boolean") b.notifications = cfg.Notifications as boolean;
    const beta = (cfg.Beta ?? {}) as Record<string, unknown>;
    if (beta.ProgramEnrollment) b.betaEnrollment = beta.ProgramEnrollment as typeof b.betaEnrollment;
    if (Array.isArray(beta.OfferPrograms)) {
      b.betaOfferPrograms = (beta.OfferPrograms as Array<Record<string, unknown>>).map((o) => ({
        description: String(o.Description ?? ""),
        token: String(o.Token ?? ""),
      }));
    }
    if (beta.RequireProgram && typeof beta.RequireProgram === "object") {
      const rp = beta.RequireProgram as Record<string, unknown>;
      b.betaRequireProgram = {
        description: String(rp.Description ?? ""),
        token: String(rp.Token ?? ""),
      };
    }
    state.behavior = b;
  }

  state.rolling = defaultRolling();
  return state;
}

// Suppress unused-symbol lints in callers that previously imported these
// from this module by re-exporting the wizard kinds.
export { SU_KIND_PINNED, SU_KIND_SETTINGS };
